import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, MessageSquareText, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { getSessionToken } from "@/utils/auth";

const BACKEND = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${getSessionToken()}` } });
const STATUS = { answered: "bg-green-100 text-green-800", escalated: "bg-amber-100 text-amber-800",
  office_replied: "bg-blue-100 text-blue-800", rate_limited: "bg-zinc-200 text-zinc-700" };

function SettingsCard({ settings, techs, onSave }) {
  const known = new Set(techs.map((t) => t.tech_id));
  const selected = settings.test_tech_ids.filter((id) => known.has(id));
  const toggleTech = (id) => onSave({ ...settings, test_tech_ids: selected.includes(id)
    ? selected.filter((x) => x !== id) : [...selected, id] });
  return (
    <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="tech-assist-settings">
      <label className="flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-zinc-900">Tech Assist is {settings.enabled ? "ON" : "OFF"}</div>
          <div className="text-xs text-zinc-500">Techs text questions to your business number and get answers from the pricebook, playbook and their own jobs (today + next 7 days).</div>
        </div>
        <input type="checkbox" checked={settings.enabled} onChange={(e) => onSave({ ...settings, enabled: e.target.checked })}
          className="w-6 h-6 accent-[#207024]" data-testid="tech-assist-enabled" />
      </label>
      <div>
        <div className="text-xs font-semibold text-zinc-700 mb-1">Only answer these techs (testing). Leave all off to answer every tech.</div>
        <div className="text-[11px] text-zinc-500 mb-1.5" data-testid="tech-assist-scope">
          {selected.length ? `Answering only ${selected.length} selected tech${selected.length > 1 ? "s" : ""}.` : "Answering every tech."}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {techs.map((t) => (
            <button key={t.tech_id} onClick={() => toggleTech(t.tech_id)} data-testid={`tech-assist-test-${t.tech_id}`}
              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${selected.includes(t.tech_id) ? "bg-[#207024] text-white border-[#207024]" : "bg-white text-zinc-700 border-zinc-300"}`}>
              {t.name}{t.phone ? "" : " (no phone)"}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function TestBox({ techs, onDone }) {
  const [techId, setTechId] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/tech-assist/test`, { tech_id: techId, question: q }, auth());
      toast.success(data.escalated ? "Escalated to the office" : "Answered and texted to the tech");
      setQ(""); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-xl border p-4 space-y-2" data-testid="tech-assist-testbox">
      <div className="font-semibold text-sm">Try a question</div>
      <p className="text-[11px] text-zinc-500">Runs exactly like a text from that tech, and the answer is really texted to them.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <select value={techId} onChange={(e) => setTechId(e.target.value)} className="h-10 rounded-lg border px-2 text-sm" data-testid="tech-assist-test-tech">
          <option value="">Pick a tech…</option>
          {techs.filter((t) => t.phone).map((t) => <option key={t.tech_id} value={t.tech_id}>{t.name}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="price for 2 sliders with petscreen"
          className="w-full sm:flex-1 h-10 rounded-lg border px-3 text-sm" data-testid="tech-assist-test-question" />
        <button onClick={run} disabled={busy || !techId || !q.trim()} data-testid="tech-assist-test-btn"
          className="h-10 px-4 rounded-lg bg-[#207024] text-white text-sm font-semibold disabled:opacity-50">{busy ? "Asking…" : "Ask"}</button>
      </div>
    </div>
  );
}

function LogRow({ r, onReplied }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/tech-assist/log/${r.log_id}/reply`, { message: reply }, auth());
      toast.success(`Texted to ${r.tech_name}`); setReply(""); onReplied();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send"); } finally { setBusy(false); }
  };
  return (
    <div className="p-3 space-y-1.5" data-testid={`tech-assist-row-${r.log_id}`}>
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="font-semibold text-zinc-900">{r.tech_name}</span>
        <span className="flex items-center gap-1.5">
          <span className={`px-2 py-0.5 rounded-full ${STATUS[r.status] || "bg-zinc-100"}`}>{r.status.replace("_", " ")}</span>
          <span className="text-zinc-400">{new Date(r.created_at).toLocaleString()}</span>
        </span>
      </div>
      <div className="text-sm text-zinc-800"><span className="text-zinc-400">Q:</span> {r.question}</div>
      {r.answer && <div className="text-sm text-zinc-700"><span className="text-zinc-400">A:</span> {r.answer}</div>}
      <div className="text-[11px] text-zinc-500">
        Sources: {(r.sources || []).join(", ") || "none"}{r.job_id ? ` · Job ${r.job_id}` : ""}{r.escalation_reason ? ` · Why escalated: ${r.escalation_reason}` : ""}
      </div>
      {r.ai_draft && <div className="text-[11px] text-zinc-500 italic">AI draft (not sent): {r.ai_draft}</div>}
      {r.office_reply && <div className="text-sm text-blue-800 bg-blue-50 rounded px-2 py-1">Office ({r.office_replied_by}): {r.office_reply}</div>}
      {r.escalated && (
        <div className="flex gap-2 pt-1">
          <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder={`Reply to ${r.tech_name} by text…`}
            className="flex-1 h-9 rounded-lg border px-3 text-sm" data-testid={`tech-assist-reply-input-${r.log_id}`} />
          <button onClick={send} disabled={busy || !reply.trim()} data-testid={`tech-assist-reply-btn-${r.log_id}`}
            className="h-9 px-3 rounded-lg bg-[#207024] text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-50">
            <Send className="w-3.5 h-3.5" /> Send
          </button>
        </div>
      )}
    </div>
  );
}

export default function TechAssistLog() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [techs, setTechs] = useState([]);
  const [log, setLog] = useState({ data: [], total: 0 });
  const [filter, setFilter] = useState("");

  const loadLog = useCallback(() => {
    axios.get(`${API}/tech-assist/log?limit=100${filter ? `&status=${filter}` : ""}`, auth()).then((r) => setLog(r.data)).catch(() => {});
  }, [filter]);
  useEffect(() => {
    axios.get(`${API}/tech-assist/settings`, auth()).then((r) => setSettings(r.data)).catch(() => {});
    axios.get(`${API}/techs`, auth()).then((r) => setTechs(r.data)).catch(() => {});
  }, []);
  useEffect(loadLog, [loadLog]);

  const save = async (next) => {
    try {
      const { data } = await axios.put(`${API}/tech-assist/settings`, next, auth());
      setSettings(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        <button onClick={() => navigate("/settings")} data-testid="back-to-settings" className="flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900">
          <ArrowLeft className="w-4 h-4" /> Back to Settings
        </button>
        <h1 className="text-xl font-bold text-zinc-900 flex items-center gap-2"><MessageSquareText className="w-6 h-6 text-[#207024]" /> Tech Assist Log</h1>
        {settings && <SettingsCard settings={settings} techs={techs} onSave={save} />}
        {settings?.enabled && <TestBox techs={techs} onDone={loadLog} />}
        <div className="bg-white rounded-xl border overflow-hidden" data-testid="tech-assist-log">
          <div className="p-3 border-b flex items-center justify-between text-sm">
            <span className="font-semibold">{log.total} questions</span>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-8 rounded border px-2 text-xs" data-testid="tech-assist-filter">
              <option value="">All</option><option value="escalated">Escalated</option><option value="answered">Answered</option>
              <option value="office_replied">Office replied</option><option value="rate_limited">Rate limited</option>
            </select>
          </div>
          <div className="divide-y">
            {log.data.length === 0 && <div className="p-6 text-center text-sm text-zinc-400">No questions yet</div>}
            {log.data.map((r) => <LogRow key={r.log_id} r={r} onReplied={loadLog} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
